import { z } from "zod";

export const chunk = z.tuple([z.literal("c"), z.string()]);
export type Chunk = z.infer<typeof chunk>;

export const end = z.tuple([z.literal("end"), z.string()]);
export type End = z.infer<typeof end>;

export const sseMessage = z.union([chunk, end]);
export type SseMessage = z.infer<typeof sseMessage>;

export async function* eventSourceAsyncGenerator(
  eventSource: EventSource
): AsyncGenerator<string> {
  const queue: string[] = [];
  let resolve: (value: IteratorResult<string, void>) => void;
  let reject: (reason: unknown) => void;

  // A promise to wait for the next item
  let nextPromise = new Promise<IteratorResult<string, void>>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  const enqueue = (event: MessageEvent<string>) => {
    try {
      const parsed = sseMessage.parse(JSON.parse(event.data));
      if (parsed[0] === "c") {
        queue.push(parsed[1]); // Enqueue chunk value
        if (resolve) {
          resolve({ value: queue.shift()!, done: false });
          nextPromise = new Promise<IteratorResult<string, void>>((res, rej) => {
            resolve = res;
            reject = rej;
          });
        }
      } else if (parsed[0] === "end") {
        if (resolve) {
          resolve({ value: undefined, done: true });
        }
      }
    } catch (err) {
      reject(err);
    }
  };

  const onError = (err: Event) => {
    if (reject) {
      reject(err);
    }
  };

  eventSource.addEventListener("message", enqueue);
  eventSource.addEventListener("error", onError);

  try {
    while (true) {
      const result = await nextPromise;
      if (result.done) break;
      yield result.value;
    }
  } finally {
    eventSource.close();
    eventSource.removeEventListener("message", enqueue);
    eventSource.removeEventListener("error", onError);
    console.log("EventSource connection closed");
  }
}
